import { skillDomainLabel } from "./skillVocabulary";

// ─── Closed vocabularies (server-owned; this file only labels them) ───────────
//
// Measured from the two producers' own constants:
//   app/services/skill_routing_wiring.py  — outcomes + wiring reasons
//   app/services/skill_router.py          — routing outcomes, refusal reasons, skip reasons
// The router's per-run record nests the second group inside `routing`; the wiring record's own
// outcome says whether the router was consulted at all.
export const WIRING_OUTCOMES = [
  "router_decision",
  "not_a_skill_task",
  "agent_config_unavailable",
];

export const ROUTER_OUTCOMES = ["selected", "refused"];

export const ROUTER_REASONS = [
  "selected",
  "capability_denied",
  "no_eligible_skill",
  "no_skill_for_task_class",
  "permitted_skill_not_eligible",
  "all_candidates_withheld",
  "registry_contract_violation",
];

export const SKIP_REASONS = [
  "not_the_task_class",
  "broader_than_needed",
  "budget_exceeded",
  "blocked",
  "not_permitted",
  "version_pinned_elsewhere",
];

const WIRING_OUTCOME_LABELS = {
  router_decision: "הוכרע על ידי Router המיומנויות",
  not_a_skill_task: "אין תחום מיומנות למשימה — ה-Router לא נשאל",
  agent_config_unavailable: "מצב החיבורים לא נקרא — לא נותב דבר",
};

const WIRING_REASON_LABELS = {
  task_class_has_no_skill_domain: "לסוג המשימה אין תחום מיומנות §28",
  agent_connections_unreadable: "מצב החיבורים של העובד לא נקרא",
};

const ROUTER_REASON_LABELS = {
  selected: "נבחרו מיומנויות",
  capability_denied: "שער ההרשאות חסם את תחום המיומנות",
  no_eligible_skill: "לא נמצאה מיומנות זכאית",
  no_skill_for_task_class: "אין מיומנות לסוג המשימה",
  permitted_skill_not_eligible: "המיומנות המותרת אינה זכאית",
  all_candidates_withheld: "כל המועמדים הושמטו",
  registry_contract_violation: "הפרת חוזה רשומת המיומנות",
};

const SKIP_REASON_LABELS = {
  not_the_task_class: "אינה מסוג המשימה",
  broader_than_needed: "רחבה מהנדרש",
  budget_exceeded: "חריגה מתקציב המיומנויות",
  blocked: "חסומה במפורש",
  not_permitted: "אינה מורשית",
  version_pinned_elsewhere: "הגרסה נעוצה במקום אחר",
};

const TASK_CLASS_LABELS = {
  m365: "Microsoft 365",
  network: "תקשורת ורשתות",
};

// C0/C1/DEL, zero-width and bidi controls, the line/paragraph separators and the BOM. Written as
// code-point ranges so this file contains no invisible literal characters of its own.
const CONTROL_RANGES = [
  [0x0000, 0x001f],
  [0x007f, 0x009f],
  [0x200b, 0x200f],
  [0x2028, 0x2029],
  [0x202a, 0x202e],
  [0x2066, 0x2069],
  [0xfeff, 0xfeff],
];

function stripControls(value) {
  let out = "";
  for (const character of value) {
    const code = character.codePointAt(0);
    if (CONTROL_RANGES.some(([start, end]) => code >= start && code <= end)) continue;
    out += character;
  }
  return out;
}

function token(value, limit) {
  if (typeof value !== "string") return null;
  const cleaned = stripControls(value).trim();
  return cleaned ? cleaned.slice(0, limit) : null;
}

function plainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function tokenList(value) {
  if (!Array.isArray(value)) return null;
  const seen = new Set();
  const out = [];
  for (const entry of value) {
    const item = token(entry, 80);
    if (item && !seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}

function normalizeSelected(entry) {
  if (!plainObject(entry)) return null;
  const skillId = token(entry.skill_id, 64);
  if (!skillId) return null;
  return {
    skillId,
    name: token(entry.name, 160),
    version: Number.isInteger(entry.version) && entry.version >= 1 ? entry.version : null,
    domain: token(entry.domain, 80),
    subdomain: token(entry.subdomain, 80),
    currentLevel:
      Number.isInteger(entry.current_level) && entry.current_level >= 0 && entry.current_level <= 100
        ? entry.current_level
        : null,
  };
}

function normalizeSkipped(entry) {
  if (!plainObject(entry)) return null;
  return {
    skillId: token(entry.skill_id, 64),
    version: Number.isInteger(entry.version) && entry.version >= 1 ? entry.version : null,
    reason: token(entry.reason, 60),
  };
}

function normalizeRouting(raw) {
  if (!plainObject(raw)) return null;
  const outcome = token(raw.outcome, 40);
  if (!ROUTER_OUTCOMES.includes(outcome)) return null;

  const selectedRaw = raw.selected;
  const skippedRaw = raw.skipped;
  if (!Array.isArray(selectedRaw) || !Array.isArray(skippedRaw)) return null;

  const selected = selectedRaw.map(normalizeSelected);
  if (selected.some((entry) => entry === null)) return null;
  // A `selected` decision with nothing selected is a contract violation, not an empty success.
  if (outcome === "selected" && selected.length === 0) return null;

  const skipped = skippedRaw.map(normalizeSkipped);
  if (skipped.some((entry) => entry === null)) return null;

  const digestRaw = token(raw.selection_digest, 128);
  const digest = digestRaw && /^[0-9a-f]{64}$/i.test(digestRaw) ? digestRaw : null;

  return {
    outcome,
    reason: token(raw.reason, 60),
    scope: token(raw.scope, 40),
    fallbackFromSubdomain: token(raw.fallback_from_subdomain, 80),
    maxSkills: Number.isInteger(raw.max_skills) && raw.max_skills >= 1 ? raw.max_skills : null,
    considered: Number.isInteger(raw.considered) && raw.considered >= 0 ? raw.considered : null,
    selected,
    skipped,
    digest,
    digestMalformed: Boolean(digestRaw) && !digest,
  };
}

/**
 * Normalize one run's `context.skill_routing` record into three honest arms:
 *
 *   absent     — the run carries no record (older runs, or a run that is not an analysis)
 *   unreadable — a payload is present but does not satisfy the producer's contract, so no part
 *                of it is interpreted (a malformed record must not be rendered as a decision)
 *   ok         — the closed wiring outcome, plus the router's nested decision when one exists
 *
 * The bijection the backend enforces (only `router_decision` carries `routing`) is enforced here
 * too, in the OPPOSITE direction: a non-router arm carrying a routing object is unreadable rather
 * than printed as two contradictory pieces of evidence.
 */
export function normalizeSkillRoutingRecord(raw) {
  if (raw === undefined || raw === null) return { availability: "absent" };
  if (!plainObject(raw)) return { availability: "unreadable" };

  const outcome = token(raw.outcome, 40);
  if (!WIRING_OUTCOMES.includes(outcome)) return { availability: "unreadable" };

  const routingRaw = raw.routing;
  if (outcome === "router_decision") {
    const routing = normalizeRouting(routingRaw);
    if (routing === null) return { availability: "unreadable" };
    return {
      availability: "ok",
      schemaVersion: Number.isInteger(raw.schema_version) ? raw.schema_version : null,
      taskClass: token(raw.task_class, 40),
      domain: token(raw.domain, 80),
      subdomain: token(raw.subdomain, 80),
      outcome,
      reason: token(raw.reason, 60),
      clearedDomains: tokenList(raw.cleared_domains),
      deniedDomains: tokenList(raw.denied_domains),
      routing,
    };
  }

  if (routingRaw !== undefined && routingRaw !== null) return { availability: "unreadable" };
  return {
    availability: "ok",
    schemaVersion: Number.isInteger(raw.schema_version) ? raw.schema_version : null,
    taskClass: token(raw.task_class, 40),
    domain: token(raw.domain, 80),
    subdomain: token(raw.subdomain, 80),
    outcome,
    reason: token(raw.reason, 60),
    // On these arms the wiring producer reports the gate as NOT consulted (`null`), never `[]`.
    clearedDomains: tokenList(raw.cleared_domains),
    deniedDomains: tokenList(raw.denied_domains),
    routing: null,
  };
}

function outcomeLabel(outcome) {
  return WIRING_OUTCOME_LABELS[outcome] || outcome;
}

function taskClassLabel(taskClass) {
  if (!taskClass) return null;
  return TASK_CLASS_LABELS[taskClass] || taskClass;
}

function reasonLabel(map, reason) {
  if (!reason) return "ללא סיבה מדווחת";
  return map[reason] || reason;
}

function Chip({ children, cls = "bg-gray-50 text-gray-600 border-gray-200" }) {
  return (
    <span className={`text-[11px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
}

function DomainChips({ label, domains }) {
  if (domains === null) {
    return (
      <p className="text-[11px] text-gray-500">
        {label}: לא דווח (השער לא נבדק).
      </p>
    );
  }
  if (domains.length === 0) {
    return (
      <p className="text-[11px] text-gray-500">
        {label}: רשימה ריקה מפורשת — לא נכלל אף תחום.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-[11px] text-gray-500">{label}:</span>
      {domains.map((domain) => (
        <Chip key={domain}>{skillDomainLabel(domain)}</Chip>
      ))}
    </div>
  );
}

function SelectedRow({ skill }) {
  return (
    <li
      data-testid={`skill-routing-selected-${skill.skillId}`}
      className="rounded-lg border border-gray-100 bg-gray-50/60 px-2.5 py-2 space-y-1"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm text-gray-800">{skill.name || skill.skillId}</span>
        {skill.version !== null && <Chip>גרסה {skill.version}</Chip>}
        {skill.currentLevel !== null && (
          <Chip cls="bg-green-50 text-green-700 border-green-200">רמה {skill.currentLevel}/100</Chip>
        )}
        {skill.domain && <Chip>{skillDomainLabel(skill.domain)}</Chip>}
        {skill.subdomain && <Chip>{skillDomainLabel(skill.subdomain)}</Chip>}
      </div>
    </li>
  );
}

function SkippedRow({ skill }) {
  return (
    <li className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-gray-600" dir="ltr">
        {skill.skillId || "מזהה לא דווח"}
      </span>
      {skill.version !== null && <span className="text-[10px] text-gray-400">גרסה {skill.version}</span>}
      <span className="text-[11px] text-amber-700">
        {reasonLabel(SKIP_REASON_LABELS, skill.reason)}
      </span>
      {skill.reason && (
        <span className="text-[10px] text-gray-400 font-mono" dir="ltr">
          {skill.reason}
        </span>
      )}
    </li>
  );
}

export default function SkillRoutingEvidencePanel({ raw }) {
  const record = normalizeSkillRoutingRecord(raw);

  if (record.availability === "absent") {
    return (
      <section
        aria-label="ראיות ניתוב מיומנות"
        data-testid="skill-routing-evidence-absent"
        className="bg-white rounded-xl border shadow-sm p-5 space-y-2"
      >
        <h2 className="font-semibold text-gray-700 text-sm border-b pb-2">ראיות ניתוב מיומנות</h2>
        <p className="text-sm text-gray-500">
          אין רשומת ניתוב מיומנות בהקשר של ריצה זו. ריצות שנכתבו לפני חיבור ה-Router, או ריצות
          שאינן ריצת ניתוח, אינן נושאות רשומה כזו.
        </p>
      </section>
    );
  }

  if (record.availability === "unreadable") {
    return (
      <section
        aria-label="ראיות ניתוב מיומנות"
        data-testid="skill-routing-evidence-unreadable"
        className="bg-white rounded-xl border shadow-sm p-5 space-y-2"
      >
        <h2 className="font-semibold text-gray-700 text-sm border-b pb-2">ראיות ניתוב מיומנות</h2>
        <p
          role="alert"
          className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
        >
          רשומת הניתוב שהתקבלה אינה מזוהה לפי החוזה — לא מוצג ממנה שום חלק ולא נגזרת ממנה בחירה או
          סירוב.
        </p>
      </section>
    );
  }

  const routing = record.routing;
  return (
    <section
      aria-label="ראיות ניתוב מיומנות"
      data-testid="skill-routing-evidence"
      className="bg-white rounded-xl border shadow-sm p-5 space-y-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2">
        <h2 className="font-semibold text-gray-700 text-sm">ראיות ניתוב מיומנות</h2>
        {record.schemaVersion !== null && (
          <span className="text-[10px] text-gray-400">סכימת רשומה {record.schemaVersion}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Chip cls="bg-blue-50 text-blue-700 border-blue-200">{outcomeLabel(record.outcome)}</Chip>
        {taskClassLabel(record.taskClass) && (
          <Chip>סוג משימה: {taskClassLabel(record.taskClass)}</Chip>
        )}
        {record.domain && <Chip>{skillDomainLabel(record.domain)}</Chip>}
        {record.subdomain && <Chip>{skillDomainLabel(record.subdomain)}</Chip>}
      </div>

      {record.outcome === "not_a_skill_task" ? (
        <p data-testid="skill-routing-wiring-note" className="text-sm text-gray-600">
          ה-Router לא נשאל: {reasonLabel(WIRING_REASON_LABELS, record.reason)}
          {record.reason && (
            <span className="text-[10px] text-gray-400 font-mono" dir="ltr"> ({record.reason})</span>
          )}
          . אין בחירה, סירוב או טביעת אצבע לרשומה הזו.
        </p>
      ) : record.outcome === "agent_config_unavailable" ? (
        <p data-testid="skill-routing-wiring-note" className="text-sm text-gray-600">
          מצב החיבורים של העובד לא נקרא, ולכן שער ההרשאות לא נבדק ולא נותב דבר.{" "}
          {reasonLabel(WIRING_REASON_LABELS, record.reason)}
          {record.reason && (
            <span className="text-[10px] text-gray-400 font-mono" dir="ltr"> ({record.reason})</span>
          )}
          . זו אינה הצהרה שום תחום חסום.
        </p>
      ) : (
        <>
          <div className="space-y-1">
            <p className="text-xs text-gray-500 font-medium">החלטת ה-Router</p>
            {routing.outcome === "selected" ? (
              <p className="text-sm text-green-700">
                נבחרו {routing.selected.length} מיומנויות מתוך {routing.considered ?? "מספר לא דווח"}{" "}
                מועמדים שנשקלו.
              </p>
            ) : (
              <p
                role="alert"
                className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
              >
                ה-Router סירב: {reasonLabel(ROUTER_REASON_LABELS, routing.reason)}
                {routing.reason && (
                  <span className="text-[10px] text-gray-500 font-mono" dir="ltr">
                    {" "}
                    ({routing.reason})
                  </span>
                )}
                . נשקלו {routing.considered ?? "מספר לא דווח"} מועמדים.
              </p>
            )}
          </div>

          {routing.outcome === "selected" && (
            <ul className="space-y-1.5" aria-label="מיומנויות שנבחרו" data-testid="skill-routing-selected-list">
              {routing.selected.map((skill) => (
                <SelectedRow key={skill.skillId} skill={skill} />
              ))}
            </ul>
          )}

          <div className="space-y-1">
            <p className="text-xs text-gray-500 font-medium">מועמדים שהושמטו</p>
            {routing.skipped.length === 0 ? (
              <p className="text-[11px] text-gray-400">לא דווחו מועמדים שהושמטו.</p>
            ) : (
              <ul className="space-y-1" aria-label="מועמדים שהושמטו">
                {routing.skipped.map((skill, index) => (
                  <SkippedRow key={skill.skillId || `skipped-${index}`} skill={skill} />
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-1 border-t border-gray-100 pt-2">
            <DomainChips label="תחומים שנקיים בשער" domains={record.clearedDomains} />
            <DomainChips label="תחומים שנחסמו בשער" domains={record.deniedDomains} />
          </div>

          <div className="space-y-0.5 border-t border-gray-100 pt-2">
            <p className="text-[11px] text-gray-500">טביעת אצבע של הבחירה (SHA-256)</p>
            {routing.digest ? (
              <p className="text-[10px] text-gray-600 font-mono break-all" dir="ltr">
                {routing.digest}
              </p>
            ) : routing.digestMalformed ? (
              <p className="text-[11px] text-amber-700">
                טביעת האצבע שדווחה אינה בפורמט SHA-256 — אינה מוצגת.
              </p>
            ) : (
              <p className="text-[11px] text-gray-400">טביעת אצבע לא דווחה.</p>
            )}
          </div>
        </>
      )}

      <p className="text-[10px] text-gray-400 border-t border-gray-100 pt-2">
        בחירת מיומנות אינה מקנה כלי, הרשאה, חיבור או אישור (§28.1). התצוגה לקריאה בלבד ואינה משנה
        דבר בריצה.
      </p>
    </section>
  );
}
